/**
 * Cashier Report Page - Refactored with DataGrid and FilterPanel
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsAPI, cashiersAPI } from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DataGrid } from '../../components/ui/DataGrid';
import { FilterPanel } from '../../components/ui/FilterPanel';
import { toast } from 'sonner';
import { 
  Wallet, Download, FileText, RefreshCw, TrendingUp, TrendingDown, Equal, Loader2, Calendar
} from 'lucide-react';

const PAGE_SIZE = 10;

export default function CashierReport() {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  
  const [report, setReport] = useState(null);
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [filteredClosings, setFilteredClosings] = useState([]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const [reportRes, cashiersRes] = await Promise.all([
        reportsAPI.getCashier(filters),
        cashiersAPI.getAll(),
      ]);
      setReport(reportRes.data);
      setCashiers(cashiersRes.data);
    } catch (err) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [report, filters]);

  const applyFilters = useCallback(() => {
    if (!report?.closings) {
      setFilteredClosings([]);
      return;
    }

    let result = [...report.closings];

    if (filters.cashier && filters.cashier !== 'all') {
      result = result.filter(c => c.cashier_id === filters.cashier);
    }

    if (filters.difference && filters.difference !== 'all') {
      result = result.filter(c => {
        if (filters.difference === 'positive') return c.difference > 0;
        if (filters.difference === 'negative') return c.difference < 0;
        if (filters.difference === 'zero') return c.difference === 0;
        return true;
      });
    }

    setFilteredClosings(result.slice(0, PAGE_SIZE));
  }, [report, filters]);

  const handleSearch = () => {
    fetchReport();
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    });
    if (report?.closings) {
      setFilteredClosings(report.closings.slice(0, PAGE_SIZE));
    }
  };

  const handleExportCSV = () => {
    if (!report?.closings?.length) return;

    const headers = ['Caixa', 'Data Fechamento', 'Valor Inicial', 'Valor Final', 'Esperado', 'Diferença'];
    const rows = report.closings.map(c => [
      getCashierName(c.cashier_id),
      c.closed_at,
      c.initial_amount,
      c.final_amount,
      c.expected_amount,
      c.difference,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-caixas-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Relatório CSV exportado!');
  };

  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      const res = await reportsAPI.getCashierPDF(filters);
      
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-caixas-${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Relatório PDF exportado!');
    } catch (err) {
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const getCashierName = (id) => cashiers.find(c => c.id === id)?.name || 'Caixa desconhecido';

  const getDifferenceIcon = (diff) => {
    if (diff > 0) return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (diff < 0) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Equal className="h-4 w-4 text-muted-foreground" />;
  };

  const filterDefinitions = [
    {
      key: 'startDate',
      label: 'Data Inicial',
      type: 'date',
    },
    {
      key: 'endDate',
      label: 'Data Final',
      type: 'date',
    },
    {
      key: 'cashier',
      label: 'Caixa',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        ...cashiers.map(c => ({ value: c.id, label: c.name })),
      ],
    },
    {
      key: 'difference',
      label: 'Diferença',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'positive', label: 'Sobra' },
        { value: 'negative', label: 'Falta' },
        { value: 'zero', label: 'Sem diferença' },
      ],
    },
  ];

  const columnDefs = useMemo(() => [
    {
      field: 'cashier_id',
      headerName: 'Caixa',
      flex: 1.5,
      cellRenderer: (params) => (
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{getCashierName(params.value)}</span>
        </div>
      ),
    },
    {
      field: 'closed_at',
      headerName: 'Fechamento',
      flex: 1.5,
      cellRenderer: (params) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span>{formatDate(params.value, true)}</span>
        </div>
      ),
    },
    {
      field: 'initial_amount',
      headerName: 'Valor Inicial',
      flex: 1,
      cellRenderer: (params) => (
        <span className="font-mono">{formatCurrency(params.value)}</span>
      ),
    },
    {
      field: 'expected_amount',
      headerName: 'Esperado',
      flex: 1,
      cellRenderer: (params) => (
        <span className="font-mono">{formatCurrency(params.value)}</span>
      ),
    },
    {
      field: 'final_amount',
      headerName: 'Valor Final',
      flex: 1,
      cellRenderer: (params) => (
        <span className="font-mono font-semibold">{formatCurrency(params.value)}</span>
      ),
    },
    {
      field: 'difference',
      headerName: 'Diferença',
      flex: 1,
      cellRenderer: (params) => (
        <div className="flex items-center gap-1">
          {getDifferenceIcon(params.value)}
          <span className={`font-mono font-semibold ${
            params.value > 0 ? 'text-emerald-500' : 
            params.value < 0 ? 'text-destructive' : ''
          }`}>
            {formatCurrency(Math.abs(params.value))}
          </span>
        </div>
      ),
    },
  ], [formatCurrency, formatDate, cashiers]);

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="cashier-report-page" data-testid="cashier-report-page">
      <div className="page-header">
        <div className="page-header-title">
          <h1>
            <Wallet className="h-6 w-6" />
            Relatório de Caixas
          </h1>
          <p>Histórico de fechamentos de caixa</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button onClick={handleExportPDF} disabled={exportingPDF}>
            {exportingPDF ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total de Fechamentos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{report.total_closings || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-emerald-500 flex items-center gap-1">
                  <TrendingUp className="h-4 w-4" />
                  Total Sobras
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-emerald-500">
                  {formatCurrency(report.total_surplus || 0)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-destructive flex items-center gap-1">
                  <TrendingDown className="h-4 w-4" />
                  Total Faltas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-destructive">
                  {formatCurrency(report.total_shortage || 0)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Saldo Líquido</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold font-mono ${
                  (report.net_difference || 0) >= 0 ? 'text-emerald-500' : 'text-destructive'
                }`}>
                  {formatCurrency(report.net_difference || 0)}
                </div>
              </CardContent>
            </Card>
          </div>

          <FilterPanel
            filters={filterDefinitions}
            values={filters}
            onChange={setFilters}
            onSearch={handleSearch}
            onClear={handleClearFilters}
          />

          <DataGrid
            title="Fechamentos de Caixa"
            columnDefs={columnDefs}
            rowData={filteredClosings}
            loading={loading}
            exportFileName="fechamentos-caixas"
            height="500px"
            getRowId={(params) => params.data.id || `${params.data.cashier_id}-${params.data.closed_at}`}
          />
        </>
      )}
    </div>
  );
}
